import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CHART_COLORS,
  currentCalendarYear,
  formatChartTooltipValue,
  parseChartYearFilter,
} from '../../chartUtils';
import { useFormCompletionSummaryQuery } from '../../hooks/useDashboardAnalytics';
import { ChartCard } from './ChartCard';
import { ChartYearFilter } from './ChartYearFilter';

type FormCompletionSummaryChartProps = {
  regionalCode: string;
};

const FORM_CONFIG = [
  { key: 'completedCn02' as const, label: 'CN 2', color: CHART_COLORS.cn02 },
  { key: 'completedCn03' as const, label: 'CN 3', color: CHART_COLORS.cn03 },
  { key: 'completedCn04' as const, label: 'CN 4', color: CHART_COLORS.cn04 },
  { key: 'completedCn05' as const, label: 'CN 5', color: CHART_COLORS.cn05 },
  { key: 'completedCn06' as const, label: 'CN 6', color: CHART_COLORS.cn06 },
];

export function FormCompletionSummaryChart({ regionalCode }: FormCompletionSummaryChartProps) {
  const [yearFilter, setYearFilter] = useState(String(currentCalendarYear()));
  const year = parseChartYearFilter(yearFilter) ?? currentCalendarYear();
  const { data, isLoading, errorMessage } = useFormCompletionSummaryQuery(regionalCode, year);

  const chartData = useMemo(() => {
    if (!data) return [];
    return FORM_CONFIG.map((item) => ({
      form: item.label,
      completed: data[item.key],
      color: item.color,
    }));
  }, [data]);

  return (
    <ChartCard
      title="Form Completion Summary (CN2–CN6)"
      subtitle="National total"
      toolbar={
        <ChartYearFilter value={yearFilter} onChange={setYearFilter} labelId="form-completion-year" />
      }
      loading={isLoading}
      error={errorMessage}
    >
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="form" />
            <YAxis />
            <Tooltip formatter={(value) => formatChartTooltipValue(value)} />
            <Bar dataKey="completed" name="Completed" radius={[6, 6, 0, 0]}>
              {chartData.map((entry) => (
                <Cell key={entry.form} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : null}
    </ChartCard>
  );
}
